// Offline support. Uses the browser's database (IndexedDB) through idb-keyval.
//
// Phase 2: the last loaded tables
//   cachedGet(url, config, enabled)  axios.get that saves good answers, and gives the saved
//                                    answer back when the internet is down
//   clearOfflineData()               removes the saved tables (logout, or offline switched off)
//   pruneOfflineData()               removes saved tables older than MAX_AGE_DAYS
//
// Phase 3: saves made offline (the outbox)
//   newClientUuid()                  a device-made ID for a new record (stops double saves)
//   addToOutbox({...})               keeps one save on this device until it can be sent
//   outboxItems(apiUrl)              this user's saves waiting to be sent (oldest first)
//   forgetOutboxItem(...)            removes one (it was saved online in the meantime)
//   discardOutboxItem(key)           removes one the user chose to throw away (Discard)
//   sendOutbox(config)               sends them, oldest first. Returns { sent, failed, loginExpired }
import axios from 'axios';
import { createStore, get, set, del, entries, values, clear } from 'idb-keyval';

const store = createStore('smartadmin-offline', 'responses');
const outbox = createStore('smartadmin-outbox', 'items');
const MAX_AGE_DAYS = 14;

// Who is logged in, read from the login token: "userId:companyId".
// No user found = nothing is saved, so one company's rows are never shown to another.
function currentScope() {
    try {
        const token = localStorage.getItem('jwt');
        if (!token) return null;
        const part = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
        const bytes = Uint8Array.from(atob(part), (c) => c.charCodeAt(0));
        const payload = JSON.parse(new TextDecoder().decode(bytes));
        // Your token has id and company_id at the top level; the others are kept just in case
        const user = payload.user || payload.data?.user || payload.data || payload;
        if (!user.id) return null;
        return `${user.id}:${user.company_id ?? ''}`;
    } catch (e) {
        return null;
    }
}

// No answer at all from the server (offline, server unreachable, timed out).
// A real server answer (even an error page) is NOT offline.
function isOfflineError(error) {
    return !error || !error.response;
}

/* ---------------- Phase 2: saved tables ---------------- */

// Returns { response, savedAt }. savedAt = null for a fresh answer, or the time the saved copy was made.
export async function cachedGet(url, config, enabled) {
    const scope = enabled ? currentScope() : null;
    const key = scope ? `${scope}|${url}` : null;

    try {
        const response = await axios.get(url, config);
        // Only good answers are saved
        if (key && response.data && response.data.status === 'success') {
            set(key, { data: response.data, savedAt: Date.now() }, store).catch(() => {});
        }
        return { response, savedAt: null };
    } catch (error) {
        if (!key || !isOfflineError(error)) throw error;
        const saved = await get(key, store).catch(() => null);
        if (!saved) throw error; // never opened online with these settings: nothing to show
        return { response: { data: saved.data }, savedAt: saved.savedAt };
    }
}

// Only the saved tables. Saves waiting in the outbox are kept, so no work is lost.
export function clearOfflineData() {
    return clear(store).catch(() => {});
}

export async function pruneOfflineData() {
    try {
        const oldest = Date.now() - MAX_AGE_DAYS * 24 * 60 * 60 * 1000;
        const all = await entries(store);
        await Promise.all(all.filter(([, v]) => !v || v.savedAt < oldest).map(([k]) => del(k, store)));
    } catch (e) {
        // storage blocked (private window): nothing to tidy
    }
}

/* ---------------- Phase 3: the outbox ---------------- */

export function newClientUuid() {
    if (window.crypto && typeof window.crypto.randomUUID === 'function') return window.crypto.randomUUID();
    // Older browsers
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
        const r = window.crypto.getRandomValues(new Uint8Array(1))[0] % 16;
        return (c === 'x' ? r : (r & 3) | 8).toString(16);
    });
}

// One entry per record: saving the same record again offline replaces the earlier copy
function outboxKey(scope, apiUrl, recordId, clientUuid) {
    return recordId ? `${scope}|edit:${apiUrl}:${recordId}` : `${scope}|new:${clientUuid}`;
}

// Keeps one save. formData = what would have been posted. preview = how the row looks in the table.
// Returns false if it couldn't be kept (no user, or storage blocked).
export async function addToOutbox({ apiUrl, formData, recordId, clientUuid, preview, dynamicFields }) {
    const scope = currentScope();
    if (!scope) return false;
    const key = outboxKey(scope, apiUrl, recordId, clientUuid);

    try {
        const fields = [...formData.entries()]; // text and files (photos are kept too)
        const old = await get(key, outbox);

        // Saved again without choosing a new file: keep the file from the earlier copy
        if (old) {
            old.entries.forEach(([name, value]) => {
                if (!(value instanceof Blob)) return;
                if (fields.some(([n, v]) => n === name && v instanceof Blob)) return;
                const i = fields.findIndex(([n]) => n === name);
                if (i >= 0) fields[i] = [name, value];
                else fields.push([name, value]);
            });
        }

        await set(key, {
            key,
            scope,
            apiUrl,
            recordId: recordId || null,
            clientUuid: clientUuid || null,
            entries: fields,
            // Plain copies (the page's data can't be stored as it is)
            preview: JSON.parse(JSON.stringify(preview || {})),
            dynamicFields: dynamicFields ? JSON.parse(JSON.stringify(dynamicFields)) : null,
            savedAt: old ? old.savedAt : Date.now(),
            error: '',      // saved again: try sending again
            refused: false,
        }, outbox);
        return true;
    } catch (e) {
        console.error('Could not keep the offline save:', e);
        return false;
    }
}

export async function outboxItems(apiUrl = null) {
    const scope = currentScope();
    if (!scope) return [];
    try {
        const all = await values(outbox);
        return all
            .filter((item) => item.scope === scope && (!apiUrl || item.apiUrl === apiUrl))
            .sort((a, b) => a.savedAt - b.savedAt);
    } catch (e) {
        return [];
    }
}

export async function forgetOutboxItem(apiUrl, recordId, clientUuid) {
    const scope = currentScope();
    if (!scope) return;
    await del(outboxKey(scope, apiUrl, recordId, clientUuid), outbox).catch(() => {});
}

export async function discardOutboxItem(key) {
    if (!key) return;
    await del(key, outbox).catch(() => {});
}

// Sends this user's saves, oldest first.
// Internet gone or login expired: stops, the rest wait for next time.
// Refused by the server (a required field, or changed by someone else): kept with the reason,
// shown as "Not sent", and not tried again until the user edits it (or discards it).
export async function sendOutbox(config) {
    const items = (await outboxItems()).filter((item) => !item.refused);
    let sent = 0;
    let failed = 0;
    let loginExpired = false;

    for (const item of items) {
        const formData = new FormData();
        item.entries.forEach(([name, value]) => formData.append(name, value));

        let response;
        try {
            response = await axios.post(item.apiUrl, formData, config);
        } catch (error) {
            if (isOfflineError(error)) break;
            item.error = 'The server had a problem with this save. It will be tried again.';
            await set(item.key, item, outbox).catch(() => {});
            failed++;
            continue;
        }

        const data = response.data || {};
        if (data.status === 'success') {
            await del(item.key, outbox).catch(() => {});
            sent++;
        } else if (['JWT_EXPIRED', 'CSRF_INVALID', 'AUTH_REQUIRED'].includes(data.code)) {
            loginExpired = true;
            break;
        } else {
            item.error = [].concat(data.message || 'The server did not accept this save.').join(' ');
            item.refused = true;
            await set(item.key, item, outbox).catch(() => {});
            failed++;
        }
    }

    return { sent, failed, loginExpired };
}